import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'

import { baseApi, RTKQueryTag } from '../../../data/baseApi'
import { updateEndpointCache } from '../../../data/rtkQuery'
import { AppDispatch } from '../../../data/store'

type GetLTClientsArgs = {
  providerId: string
  status: 'active' | 'inactive'
  programName?: string
}

export type ClientsInProgramFilter = 'all' | 'interacted_with'
export type DBTTeensRelationshipFilter = 'primary' | ClientsInProgramFilter

type GetClientsInProgramArgs = {
  programName: string
  interactionMode: ClientsInProgramFilter
  providerId?: string
}

type GetLTClientsCountsArgs = {
  providerId: string
  excludeContentGroups: string[]
}

type Count = {
  overdue_responses: number
  patient_id: string
  provider_id: string
  unread_messages: number
  unread_responses: number
}

// Optimistically update "clients counts", which controls the unreviewed activities dashlet
export const updateGetClientsCountsCache = (
  dispatch: AppDispatch,
  userId: string | undefined,
  clientDetails: ClientListClientObject | undefined,
) => {
  dispatch(
    updateEndpointCache(ltClientsApi, 'getClientsCounts')
      .where({ providerId: userId })
      .build((draft) => {
        const clientId = clientDetails?.id
        if (!clientId || !draft || !draft[clientId]) {
          return
        }
        draft[clientId].forEach((count) => {
          count.unread_responses = Math.max(0, count.unread_responses - 1)
        })
      }),
  )
}

const ltClientsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getClients: builder.query<ClientListClientObject[], GetLTClientsArgs>({
      query: ({ providerId, status, programName }) => ({
        method: 'get',
        url: `/v2/clients`,
        params: { providerId, status, program_name: programName },
      }),
      transformResponse: (response: { clients: ClientListClientObject[] }) => {
        return response.clients
      },
      providesTags: [RTKQueryTag.CLIENT, RTKQueryTag.PERSIST],
    }),
    getClientsInProgram: builder.query<ClientListClientObject[], GetClientsInProgramArgs>({
      query: ({ programName, interactionMode, providerId }) => ({
        method: 'get',
        url: `/v2/clients-in-program`,
        params: {
          program_name: programName,
          interaction_mode: interactionMode,
          ...(interactionMode === 'interacted_with' && providerId ? { providerId } : {}),
        },
      }),
      transformResponse: (response: { clients: ClientListClientObject[] }) => {
        return response.clients
      },
      providesTags: [RTKQueryTag.CLIENT, RTKQueryTag.PERSIST],
    }),
    getClientsCounts: builder.query<{ [key: string]: Count[] }, GetLTClientsCountsArgs>({
      query: ({ providerId, excludeContentGroups }) => ({
        method: 'post',
        url: `/lt/v1/counts`,
        data: { provider_ids: [providerId], exclude_content_groups: excludeContentGroups },
      }),
      providesTags: [RTKQueryTag.CLIENT, RTKQueryTag.PERSIST],
    }),
  }),
})

export const { useGetClientsQuery, useGetClientsInProgramQuery, useGetClientsCountsQuery } = ltClientsApi
export default ltClientsApi
